

























































import { defineComponent, ref } from '@vue/composition-api';
import { Scrollbar } from '@/app/components';
import { PlusIcon, MinusIcon } from '@vue-hero-icons/solid';

export default defineComponent({
    name: 'Faq',
    metaInfo: { title: 'Faq' },
    components: {
        Scrollbar,
        PlusIcon,
        MinusIcon,
    },
    setup() {
        type FAQ = {
            question: string;
            answers: {
                paragraph: string;
                bulletPoints?: string[];
            }[];
            hints?: string[];
            open: boolean;
        };

        // Sample FAQs
        const faqQuestions = ref<FAQ[]>([
            {
                question: 'How do I create and manage data collection pipelines?',
                answers: [
                    {
                        paragraph:
                            'To create a pipeline, click the “Create” button on the Data Collection Pipelines page, then provide details like the title, description, and processing steps.',
                    },
                    {
                        paragraph:
                            'Every pipeline includes mandatory steps like data collection and loading. Optional steps include data mapping, data cleaning, and data confidentiality.',
                    },
                ],
                hints: ['Pipelines can also be deleted if no longer needed, or duplicated for similar configurations'],
                open: false,
            },
            {
                question: 'What data collection methods are supported by the platform?',
                answers: [
                    {
                        paragraph: 'The platform supports:',
                    },
                    {
                        paragraph: 'File Uploads: Formats like .csv, .json,',
                    },
                    {
                        paragraph: 'API Data Retrieval,',
                    },
                    {
                        paragraph: 'Other File Formats: .jpg, and .pdf. for specific organizational needs.',
                    },
                ],
                hints: [
                    'For the API Data Retrieval, select the API option and set up the URL, authentication type (e.g., API Key, OAuth), request method (e.g., GET, POST), and required parameters. Scheduling periodic updates is also supported.',
                ],
                open: false,
            },
            {
                question: 'How do I upload files, including large ones, to a pipeline?',
                answers: [
                    {
                        paragraph:
                            'During data collection pipeline setup, choose File Upload and provide both a sample and the actual file. The platform efficiently handles large datasets and complex structures like structured .json files.',
                    },
                ],
                hints: [
                    'Users can also take advantage of the “Append Data” option, which allows for the addition of new data to an existing dataset without overwriting previous uploads.',
                ],
                open: false,
            },
            {
                question: 'What happens if I encounter issues during data collection?',
                answers: [
                    {
                        paragraph:
                            'For failed file uploads, check the format and size, and verify your connection. For API errors, ensure the URL and credentials are correct.',
                    },
                ],
                open: false,
            },
            {
                question: 'How do I begin the data mapping process?',
                answers: [
                    {
                        paragraph:
                            'After data collection, the platform guides you to start mapping. The REDOL CIM is available, and you can browse its concepts to begin the process.',
                    },
                    {
                        paragraph: 'In the mapping interface:',
                        bulletPoints: [
                            'REDOL CIM fields are shown on the left.',
                            'Your dataset fields appear in the center.',
                        ],
                    },
                ],
                hints: [
                    'To simplify the process, the platform offers support, mapping recommendations, confidence levels, drag-and-drop functionality, field descriptions, and sample data displays',
                    'If a dataset field doesn’t match a REDOL CIM concept, you can leave it unmapped, add a description for later reference, or consult REDOL support for help.',
                ],
                open: false,
            },
            {
                question: 'During Data Cleaning, what rules can I define?',
                answers: [
                    {
                        paragraph: 'You can establish rules for:',
                        bulletPoints: [
                            'Detecting and correcting outliers.',
                            'Handling missing values.',
                            'Ensuring consistency in data formats.',
                        ],
                    },
                ],
                hints: [
                    'Multiple cleaning rules can be applied to a single field for thorough validation and correction.',
                ],
                open: false,
            },
            {
                question: 'How does the REDOL DSP handle data confidentiality?',
                answers: [
                    {
                        paragraph:
                            'Data Confidentiality ensures that users can protect personally identifiable information (PII) by applying specific rules to data fields. Fields can be categorized as "identifying," "quasi-identifying," or "sensitive." Identifying fields are removed, while quasi-identifying fields are generalized using k-anonymity techniques like masking for text and categorization for numeric data.',
                    },
                ],
                open: false,
            },
            {
                question: 'What metadata can I specify during data collection?',
                answers: [
                    {
                        paragraph:
                            'You can add descriptions, tags, file formats, and other metadata to enhance discoverability and provide context for datasets.',
                    },
                ],
                open: false,
            },
            {
                question: 'How can I manage access policies for my data assets in REDOL DSP?',
                answers: [
                    {
                        paragraph:
                            'The platform allows you to set data asset visibility to either "Public" or "Authorized." Public assets are accessible to all users without restrictions, while Authorized assets are limited to users who meet specific access policy requirements.',
                    },
                ],
                hints: [
                    'For Authorized access, you can define control methods (e.g., Deny-all) and specify exceptions by setting parameters such as user conditions and values.',
                ],
                open: false,
            },
            {
                question: 'How can I search for data assets in the Data Repository?',
                answers: [
                    {
                        paragraph:
                            'Users can explore the Data Repository with ease using advanced filters based on data type, ownership, and other criteria. A powerful free-text search feature enables quick identification of specific assets, while detailed metadata evaluations help pinpoint the most relevant datasets.',
                    },
                ],
                hints: [
                    'You can only view data assets that you own or have acquired, in accordance with your access rights. Assets stored locally or restricted by providers will not be visible.',
                ],
                open: false,
            },
            {
                question: 'How do I retrieve a data asset after a contract is approved?',
                answers: [
                    {
                        paragraph:
                            'Once a contract is approved, initiate a retrieval request by specifying parameters such as title, description, and acquisition method. You can also choose specific concepts, apply filters, and provide indicative values to refine your request.',
                    },
                ],
                hints: [
                    'Once the retrieval request is configured, a sample preview of the results is displayed to ensure accuracy.',
                ],
                open: false,
            },
            {
                question: 'How do I access the retrieval results?',
                answers: [
                    {
                        paragraph:
                            'After configuring your retrieval request, you will receive instructions for authentication and API endpoints. Use a generated access token to access the data through GET or POST methods.',
                    },
                ],
                hints: [
                    'You can retrieve assets individually or in a bundle, depending on your agreement with the data provider.',
                ],
                open: false,
            },
            {
                question: 'How do I initiate a contract for acquiring data from the Data Marketplace?',
                answers: [
                    {
                        paragraph:
                            'In the Data Marketplace, the process begins when a data consumer submits a request to acquire a specific data asset. Once the request is sent, the data provider reviews it, including the data structure and field selection, to ensure it aligns with what they can offer.',
                    },
                ],
                open: false,
            },
            {
                question: 'How is a contract finalized and signed?',
                answers: [
                    {
                        paragraph:
                            'Before reaching an agreement, both data providers and data consumers enter the negotiation phase. Once both parties agree on the contract terms, it is signed on the blockchain by both the provider and consumer. Following signature and payment processing, the contract is executed, granting the consumer access to the data.',
                    },
                ],
                open: false,
            },
            {
                question: 'How is my contract monitored after it is signed on the REDOL DSP?',
                answers: [
                    {
                        paragraph:
                            'Once a contract is signed and active, both the data asset provider and consumer can monitor its status through the platform. The system tracks key elements such as compliance with the contract terms, payment status, usage rights, and the duration of the contract If the contract is nearing expiration or if either party wishes to modify its terms—such as extending the duration or adjusting conditions—justification for the changes must be provided. Once the modifications are proposed, the updated contract requires approval and signature from both parties to become effective.',
                    },
                ],
                hints: [
                    'If there are any breaches or discrepancies with the contract terms, both parties will be alerted in order to review and address the issue, ensuring compliance and maintaining the integrity of the agreement.',
                ],
                open: false,
            },
            {
                question: 'How can I update my profile information in the REDOL DSP?',
                answers: [
                    {
                        paragraph:
                            'You can update your profile details by navigating to your profile settings page, where you can edit personal information, contact details, and preferences.',
                    },
                ],
                hints: [
                    'To change your password or update security settings, go to your profile settings page and select the relevant options under security to modify your credentials.',
                ],
                open: false,
            },
            {
                question: 'How do I manage notifications for my REDOL DSP account?',
                answers: [
                    {
                        paragraph:
                            'You can configure your notification preferences in the settings menu, where you can opt to receive alerts via email or on the platform for important events related to your data assets and contracts.',
                    },
                ],
                hints: [
                    'Notifications include updates on contract status, data asset activity, request approvals, and any changes to data asset access or policies, ensuring you are always informed.',
                ],
                open: false,
            },
            {
                question: "How can I update my organization's details on REDOL DSP?",
                answers: [
                    {
                        paragraph:
                            "To update your organization's information, visit the organization settings page where you can modify details such as the organization name, contact information, and associated members.",
                    },
                ],
                hints: [
                    'As an organization admin, you can manage user roles and permissions by adding or removing users within the organization.',
                ],
                open: false,
            },
            {
                question: "How do I manage my organization's data-sharing agreements and contracts?",
                answers: [
                    {
                        paragraph:
                            'All contracts involving your organization can be accessed and managed through the "Contracts" section, where you can view, track, and monitor the status of all agreements and transactions related to your data assets.',
                    },
                ],
                open: false,
            },
        ]);

        const toggleFaq = (index: number) => {
            faqQuestions.value[index].open = !faqQuestions.value[index].open;
        };

        return { faqQuestions, toggleFaq };
    },
});
